/**
 * CORE_001 — Device & viewport adaptation foundation.
 *
 * `useViewport()` is the single source of truth for viewport-driven UI
 * decisions. It reports MEASURED capabilities (not device names) so the
 * UI can adapt to any phone, tablet, foldable, or desktop without
 * hard-coded model assumptions:
 *
 *   - `vw` / `vh`       layout viewport in px (window.innerWidth/Height)
 *   - `visualVH`        visual viewport height (px) — shrinks when the
 *                       on-screen keyboard pushes content up
 *   - `keyboardOpen`    boolean — true when the keyboard is visibly
 *                       reducing the visual viewport (>=120 px gap)
 *   - `kbInset`         the px difference (`vh - visualVH`) clamped to 0
 *   - `orientation`     "portrait" | "landscape"
 *   - `isTouch`         true when the device reports a coarse pointer
 *   - `safeTop` / `safeBottom` / `safeLeft` / `safeRight`
 *                       runtime-resolved env(safe-area-inset-*) values
 *
 * Side-effects: this hook also keeps the global CSS variables
 *   --vvh-px      = visual viewport height (with px unit)
 *   --kb-inset    = keyboard inset (with px unit)
 * in sync, so plain CSS / Tailwind utilities (`min-h-vvh`, `pb-kb`) can
 * react WITHOUT each component subscribing to the hook.
 *
 * The CSS variables defined in index.css act as fallbacks when this
 * hook isn't mounted (SSR, first paint, error boundaries).
 *
 * Performance: handlers are passive listeners, debounced via rAF, and
 * cleaned up on unmount. No polling.
 */
import { useEffect, useState } from "react";

const isBrowser = typeof window !== "undefined";

function readSafeArea(side) {
  if (!isBrowser || !window.getComputedStyle) return 0;
  // We expose env(safe-area-inset-*) through CSS vars in index.css; read
  // them via getComputedStyle so the value reflects whatever the browser
  // actually negotiated (notch / home indicator / split view).
  const style = window.getComputedStyle(document.documentElement);
  const raw = style.getPropertyValue(`--safe-${side}`).trim();
  const px = parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

function snapshot() {
  if (!isBrowser) {
    return {
      vw: 0, vh: 0, visualVH: 0, keyboardOpen: false, kbInset: 0,
      orientation: "portrait", isTouch: false,
      safeTop: 0, safeBottom: 0, safeLeft: 0, safeRight: 0,
    };
  }
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  const visualVH = window.visualViewport?.height ?? vh;
  const kbInset = Math.max(0, vh - visualVH);
  // 120 px is the empirical floor: smaller deltas are usually browser
  // toolbars (Safari URL bar) collapsing, NOT the keyboard. iOS/Android
  // keyboards always exceed this on every supported device.
  const keyboardOpen = kbInset >= 120;
  const orientation = vw >= vh ? "landscape" : "portrait";
  const isTouch = (window.matchMedia?.("(pointer: coarse)").matches) ?? false;

  return {
    vw, vh, visualVH, keyboardOpen, kbInset, orientation, isTouch,
    safeTop: readSafeArea("top"),
    safeBottom: readSafeArea("bottom"),
    safeLeft: readSafeArea("left"),
    safeRight: readSafeArea("right"),
  };
}

function writeCssVars(state) {
  if (!isBrowser) return;
  const root = document.documentElement;
  // visualVH and kbInset are the only values that change frequently
  // enough to warrant pushing back into CSS. The safe-area values come
  // from env() and need no JS write.
  root.style.setProperty("--vvh-px", `${Math.round(state.visualVH)}px`);
  root.style.setProperty("--kb-inset", `${Math.round(state.kbInset)}px`);
}

export default function useViewport() {
  const [state, setState] = useState(() => snapshot());

  useEffect(() => {
    if (!isBrowser) return undefined;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = snapshot();
        writeCssVars(next);
        setState((prev) => {
          // Skip re-render when nothing changed (avoid storms during
          // smooth scrolls that don't actually move the keyboard).
          if (
            prev.vw === next.vw && prev.vh === next.vh &&
            prev.visualVH === next.visualVH &&
            prev.orientation === next.orientation
          ) return prev;
          return next;
        });
      });
    };
    update();
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", update, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", update, { passive: true });
      window.visualViewport.addEventListener("scroll", update, { passive: true });
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", update);
        window.visualViewport.removeEventListener("scroll", update);
      }
    };
  }, []);

  return state;
}
